import type { ProjectContextTerm } from '@ai-meeting/shared'

/**
 * Terms imported from a CSV file the reader picked.
 *
 * Two columns, `source,target`, one pair per line. Quoted cells are honoured so
 * a term may contain a comma. A leading header row is dropped when its first
 * cell names the column rather than a term — the project dictionary stores no
 * header, so a file exported from the design's sample keeps working.
 *
 * Everything lands as `targetLang: '*'` for the same reason the add dialog does:
 * the browser never sends a target language, so a pair is direction-agnostic
 * until someone narrows it.
 */
const HEADER_WORDS = new Set([
  'source',
  'term',
  'term (source)',
  'thuật ngữ',
  'thuat ngu',
  'nguồn',
  '用語',
  '原語',
  'ja',
  'japanese',
])

/** Split one CSV line on commas, keeping quoted commas and stripping quotes. */
function splitCsvLine(line: string): string[] {
  const cells: string[] = []
  let cell = ''
  let quoted = false

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        cell += '"'
        index += 1
      } else {
        quoted = !quoted
      }
      continue
    }
    if (char === ',' && !quoted) {
      cells.push(cell)
      cell = ''
      continue
    }
    cell += char
  }
  cells.push(cell)
  return cells
}

export function parseDictionaryCsv(text: string): ProjectContextTerm[] {
  const lines = text.split(/\r?\n/)
  const terms: ProjectContextTerm[] = []
  const seen = new Set<string>()

  for (const line of lines) {
    if (!line.trim()) continue
    const cells = splitCsvLine(line)
    const source = (cells[0] ?? '').trim()
    const target = (cells[1] ?? '').trim()

    if (terms.length === 0 && HEADER_WORDS.has(source.toLowerCase())) continue
    if (!source) continue

    const key = source.normalize('NFKC').toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)

    terms.push(
      target
        ? { source, target, targetLang: '*', status: 'ACTIVE' }
        : { source, targetLang: '*', status: 'ACTIVE' }
    )
  }

  return terms
}
